/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Render the rail with the teacher workspace selector, role-scoped navigation, help, inbox and account entries.
 * @state-owner navigation store; overlay host owns popovers
 * @allowed-imports React; workspace store; dataset store; shared components; role provider; Agent A workspace selection composition
 * @forbidden in-product role switching; API client; module internals
 * @public-api WorkspaceShell
 * @side-effects navigation and popover opening
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import type { MouseEvent } from 'react';
import { DesktopWorkspaceSwitcher } from '../../app/feature-composition/agent-a/WorkspaceSwitcher';
import { useActiveRole } from '../../app/providers/ActiveRoleProvider';
import { useWorkspaceSession } from '../../app/providers/SessionProvider';
import { VispoAnimatedMark } from '../../shared/components/BrandMarks';
import { railAnchor, useOverlay } from '../../shared/components/OverlayHost';
import { PopoverIcon } from '../../shared/components/VispoIcon';
import type { WorkspaceSection } from '../../shared/lib/model';
import { liveRuns } from '../../shared/lib/selectors';
import { workspaceNavigationSections } from '../../shared/lib/workspace-navigation';
import { useDatasetStore } from '../../shared/model/dataset-store';
import { useTranslator } from '../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../shared/model/workspace-store';
import { AccountPopover, HelpPopover, InboxPopover } from './ShellPopovers';

export function WorkspaceRail({ onNavigate }: { onNavigate(section: WorkspaceSection): void }) {
  const role = useActiveRole();
  const { session } = useWorkspaceSession();
  const accountLabel =
    session?.displayName || session?.email || (role === 'teacher' ? '老師' : '學生');
  const t = useTranslator();
  const section = useWorkspaceUiStore((state) => state.section);
  const runs = useDatasetStore((state) => state.runs);
  const seats = useDatasetStore((state) => state.seats);
  const { openPopover } = useOverlay();

  const needsAttention =
    role === 'teacher'
      ? liveRuns(runs).reduce(
          (total, run) =>
            total +
            (seats[run.classIndex] ?? []).filter(
              (seat) => seat.status === 'need' || seat.status === 'stuck',
            ).length,
          0,
        )
      : 0;

  const open = (
    event: MouseEvent<HTMLElement>,
    content: React.ReactNode,
    variant: 'default' | 'inbox' = 'default',
  ) => {
    event.stopPropagation();
    const point = railAnchor(event);
    openPopover({
      content,
      x: point.x,
      y: point.y,
      className: `rail-popover${variant === 'inbox' ? ' rail-popover--inbox' : ''}`,
    });
  };

  return (
    <nav className="rail" id="rail">
      {role === 'teacher' ? (
        <div className="ws ws--switcher">
          <DesktopWorkspaceSwitcher />
        </div>
      ) : (
        <div aria-hidden="true" className="ws">
          <VispoAnimatedMark className="fb" />
        </div>
      )}
      {workspaceNavigationSections(role).map((item) => (
        <button
          aria-label={t(item.label)}
          className={`ri ${section === item.k ? 'on' : ''}`}
          key={item.k}
          type="button"
          onClick={() => onNavigate(item.k as WorkspaceSection)}
        >
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d={item.path} />
          </svg>
          <span>{t(item.label)}</span>
          {item.k === 'live' && needsAttention ? (
            <span className="bdg">{needsAttention}</span>
          ) : null}
        </button>
      ))}
      <div className="rsp" />
      <button
        aria-label={t('說明')}
        className="rb"
        title={t('說明')}
        type="button"
        onClick={(event) => open(event, <HelpPopover />)}
      >
        <PopoverIcon name="help" />
      </button>
      <button
        aria-label={t('收件匣')}
        className="rb"
        id="rbInbox"
        title={t('收件匣')}
        type="button"
        onClick={(event) => open(event, <InboxPopover onNavigate={onNavigate} />, 'inbox')}
      >
        <PopoverIcon name="mail" />
        <span className="dot" />
      </button>
      <button
        aria-label={accountLabel}
        className="rav"
        type="button"
        onClick={(event) => open(event, <AccountPopover />)}
      >
        {accountLabel.trim().charAt(0).toUpperCase()}
      </button>
    </nav>
  );
}
